import { OutreachProgram } from "../types/outreach";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Users, TrendingUp, Activity, FileText } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface ProgramReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  program: OutreachProgram;
}

export function ProgramReportDialog({
  open,
  onOpenChange,
  program,
}: ProgramReportDialogProps) {
  const participationRate = program.actualParticipants
    ? (program.actualParticipants / program.targetParticipants) * 100
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Program Report</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold">{program.name}</h3>
            <p className="text-sm text-muted-foreground">
              {format(new Date(program.startDate), "MMM d, yyyy")} -{" "}
              {program.endDate
                ? format(new Date(program.endDate), "MMM d, yyyy")
                : "Ongoing"}
            </p>
          </div>

          <div className="grid gap-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>Participation Rate</span>
                </div>
                <span className="font-medium">{participationRate.toFixed(1)}%</span>
              </div>
              <Progress value={participationRate} className="h-2" />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>
                  {program.actualParticipants || 0} actual participants
                </span>
                <span>{program.targetParticipants} target</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 rounded-lg border p-4">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Status</span>
                </div>
                <Badge
                  variant={
                    program.status === "completed"
                      ? "outline"
                      : program.status === "active"
                      ? "default"
                      : "secondary"
                  }
                  className="capitalize"
                >
                  {program.status}
                </Badge>
              </div>

              <div className="space-y-2 rounded-lg border p-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Progress</span>
                </div>
                <p className="font-medium">
                  {program.status === "completed"
                    ? "100%"
                    : program.status === "active"
                    ? "In Progress"
                    : "Not Started"}
                </p>
              </div>
            </div>

            {program.description && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Program Notes
                  </span>
                </div>
                <p className="whitespace-pre-wrap rounded-lg border p-4">
                  {program.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
